import React from "react";
import { requireExhaustive } from "../../../../../shared/src/util";
import { CRMTriageEmailSingleViewUnassigned } from "../CRMTriageEmailSingleViewUnassigned/CRMTriageEmailSingleViewUnassigned";
import { CRMInboxTable } from "../CRMInboxTable/CRMInboxTable";
import { TTriageBulkEmailMarkAsIrrelevantForm, TTriageEmailAssignToCaseSearchForm, TTriageEmailMarkAsIrrelevantForm, TTriageForm, TTriageSimpleEmailForm, TTriageEmailAssignToCaseAndUserForm, TTriageEmailAssignToCaseForm, TTriageEmailAssignToUserOnlyForm, TDetailedEmailForm, TTriageActiveTypeVisible, TTriageEmailMarkAsResolvedForm } from "../../../../../domain/codecs/form/TriageForm";
import { TGetStaffInboxesForm } from "../../../../../domain/codecs/form/StaffAwayForm";
import { CRMDropdownControlledBlock } from "../CRMDropdownControlledBlock/CRMDropdownControlledBlock";
import { useTriage } from "../../../hooks/useTriage";
import { CRMTriageEmailSingleViewExpired } from "../CRMTriageEmailSingleViewExpired/CRMTriageEmailSingleViewExpired";
import { CRMBlockScrollCacheView, CRMBlockScrollResetView } from "../../CRM/CRMBlock/CRMBlockViews/CRMBlockViews";
import { CRMStaffInboxesContent } from "../CRMStaffInboxesContent/CRMStaffInboxesContent";

type TTriageInboxProps = {
    unassignedForm: TTriageForm;
    expiredForm: TTriageForm;
    staffInboxesForm: TGetStaffInboxesForm;
    staffInboxEmailsForm: TTriageForm;
    triageTypeVisible: TTriageActiveTypeVisible;
    onChangeTriageTypeVisible: (triageTypeVisible: TTriageActiveTypeVisible) => void;
    onArchive: (form: TTriageEmailMarkAsIrrelevantForm, triageType: TTriageActiveTypeVisible) => void;
    onResolve: (form: TTriageEmailMarkAsResolvedForm, triageType: TTriageActiveTypeVisible) => void;
    onChangeChaseModeForm: (form: TTriageEmailMarkAsResolvedForm) => void;
    onBulkArchive: (form: TTriageBulkEmailMarkAsIrrelevantForm, triageType: TTriageActiveTypeVisible) => void;
    onAssignToPerson: (form: TTriageEmailAssignToUserOnlyForm, triageType: TTriageActiveTypeVisible) => void;
    onAssignToCaseAndMe: (form: TTriageEmailAssignToCaseAndUserForm, triageType: TTriageActiveTypeVisible) => void;
    onAssignToCaseAndStaff: (form: TTriageEmailAssignToCaseAndUserForm, triageType: TTriageActiveTypeVisible) => void;
    onAssignToCaseAndHandler: (form: TTriageEmailAssignToCaseAndUserForm, triageType: TTriageActiveTypeVisible) => void;
    onAssignToCaseAndResolve: (form: TTriageEmailAssignToCaseForm, triageType: TTriageActiveTypeVisible) => void;
    onOpenEmail: (email: TTriageSimpleEmailForm, triageType: TTriageActiveTypeVisible) => void;
    onReply: (form: TDetailedEmailForm) => void;
    onForward: (form: TDetailedEmailForm) => void;
    onFileAttachments: (form: TDetailedEmailForm) => void;
    onLoadMore: (triageType: TTriageActiveTypeVisible) => void;
    onChangeImportanceBand: (band: string) => void;
    onChangeSearchCaseQuery: (form: TTriageEmailAssignToCaseSearchForm, triageType: TTriageActiveTypeVisible) => void;
    onSelectStaffUser: (userId: string) => void;
    onToggleAway: (payload: { user_id: string; is_away: boolean }) => void;
};

export const CRMTriageInbox = (props: React.PropsWithChildren<TTriageInboxProps>): JSX.Element => {
    const {
        getTriageFormToUse,
        isPresentEmailSetToOpen,
        getOpenedEmail,
        isShowingNext,
        isShowingPrevious,
        onOpenEmail,
        onArchive,
        onResolve,
        onBulkArchive,
        onLoadMore,
        onChangeSearchText,
        closeSingleView,
        onNext,
        onPrevious,
        onAssignToCaseAndHandler,
        onAssignToCaseAndMe,
        onAssignToCaseAndResolve,
        onAssignToCaseAndStaff,
        onAssignToPerson,
    } = useTriage<TTriageActiveTypeVisible>({
        getTriageFormToUseCallback: (triageTypeVisible) =>
            triageTypeVisible === "UNASSIGNED" ? props.unassignedForm
            : triageTypeVisible === "EXPIRED" ? props.expiredForm
            : triageTypeVisible === "STAFF_INBOXES" ? props.staffInboxEmailsForm
            : requireExhaustive(triageTypeVisible),
        ...props,
    });

    const isUnassignedEmailOpen = (): boolean =>
        isPresentEmailSetToOpen() && props.triageTypeVisible === "UNASSIGNED";

    const isExpiredEmailOpen = (): boolean =>
        isPresentEmailSetToOpen() && props.triageTypeVisible === "EXPIRED";

    const isStaffInboxEmailOpen = (): boolean =>
        isPresentEmailSetToOpen() && props.triageTypeVisible === "STAFF_INBOXES";

    return (
        <>
            {/* TABLE */}
            <CRMBlockScrollCacheView isShowing={!isPresentEmailSetToOpen()}>
                <CRMDropdownControlledBlock
                    menuMode="full_width"
                    sections={[
                        {
                            value: "UNASSIGNED",
                            label: "Unassigned",
                            badgeCount: props.unassignedForm.children.counts.available_count,
                            content: 
                                <CRMInboxTable
                                    form={props.unassignedForm}
                                    onOpenEmail={onOpenEmail}
                                    onBulkArchive={onBulkArchive}
                                    onLoadMore={onLoadMore}
                                    onChangeImportanceBand={props.onChangeImportanceBand}
                                />
                        },
                        {
                            value: "EXPIRED",
                            label: "Expired",
                            badgeCount: props.expiredForm.children.counts.available_count,
                            content:
                                <CRMInboxTable
                                    form={props.expiredForm}
                                    onOpenEmail={onOpenEmail}
                                    onBulkArchive={onBulkArchive}
                                    onLoadMore={onLoadMore}
                                />
                        },
                        {
                            value: "STAFF_INBOXES",
                            label: "Staff Inboxes",
                            badgeCount: props.staffInboxesForm.children.staff.filter((m) => m.is_away).length,
                            content:
                                <CRMStaffInboxesContent
                                    staffInboxesForm={props.staffInboxesForm}
                                    staffInboxEmailsForm={props.staffInboxEmailsForm}
                                    selectedUserId={props.staffInboxEmailsForm.edited.target_user_id || ""}
                                    onSelectUser={props.onSelectStaffUser}
                                    onToggleAway={props.onToggleAway}
                                    onOpenEmail={onOpenEmail}
                                    onBulkArchive={onBulkArchive}
                                    onLoadMore={onLoadMore}
                                />
                        },
                    ]}
                    value={props.triageTypeVisible}
                    onValueChange={props.onChangeTriageTypeVisible}
                />
            </CRMBlockScrollCacheView>
            
            {/* SINGLE VIEWS */}
            <CRMBlockScrollResetView isShowing={isPresentEmailSetToOpen()}>
                {/* SINGLE VIEW - UNASSIGNED */}
                {isUnassignedEmailOpen() &&
                    <CRMTriageEmailSingleViewUnassigned
                        count={getTriageFormToUse().children.counts.available_count}
                        onChangeSearchText={onChangeSearchText}
                        email={getOpenedEmail()}
                        isShowingNext={isShowingNext()}
                        isShowingPrevious={isShowingPrevious()}
                        onClose={closeSingleView}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        onArchive={onArchive}
                        onAssignToCaseAndMe={onAssignToCaseAndMe}
                        onAssignToCaseAndStaff={onAssignToCaseAndStaff}
                        onAssignToCaseAndHandler={onAssignToCaseAndHandler}
                        onAssignToCaseAndResolve={onAssignToCaseAndResolve}
                        onAssignToPerson={onAssignToPerson}
                        onReply={props.onReply}
                        onForward={props.onForward}
                        onFileAttachments={props.onFileAttachments}
                    />
                }

                {/* SINGLE VIEW - STAFF INBOX */}
                {isStaffInboxEmailOpen() &&
                    <CRMTriageEmailSingleViewUnassigned
                        count={getTriageFormToUse().children.counts.available_count}
                        onChangeSearchText={onChangeSearchText}
                        email={getOpenedEmail()}
                        isShowingNext={isShowingNext()}
                        isShowingPrevious={isShowingPrevious()}
                        onClose={closeSingleView}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        onArchive={onArchive}
                        onAssignToCaseAndMe={onAssignToCaseAndMe}
                        onAssignToCaseAndStaff={onAssignToCaseAndStaff}
                        onAssignToCaseAndHandler={onAssignToCaseAndHandler}
                        onAssignToCaseAndResolve={onAssignToCaseAndResolve}
                        onAssignToPerson={onAssignToPerson}
                        onReply={props.onReply}
                        onForward={props.onForward}
                        onFileAttachments={props.onFileAttachments}
                    />
                }

                {/* SINGLE VIEW - EXPIRED */}
                {isExpiredEmailOpen() &&
                    <CRMTriageEmailSingleViewExpired
                        count={getTriageFormToUse().children.counts.available_count}
                        onChangeSearchText={onChangeSearchText}
                        email={getOpenedEmail()}
                        isShowingNext={isShowingNext()}
                        isShowingPrevious={isShowingPrevious()}
                        onClose={closeSingleView}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        onResolve={onResolve}
                        onChangeChaseModeForm={props.onChangeChaseModeForm}
                        onAssignToCaseAndMe={onAssignToCaseAndMe}
                        onAssignToCaseAndStaff={onAssignToCaseAndStaff}
                        onAssignToCaseAndHandler={onAssignToCaseAndHandler}
                        onAssignToCaseAndResolve={onAssignToCaseAndResolve}
                        onAssignToPerson={onAssignToPerson}
                        onReply={props.onReply}
                        onForward={props.onForward}
                        onFileAttachments={props.onFileAttachments}
                    />
                }
            </CRMBlockScrollResetView>
        </>
    );
};
